import React, { useState } from "react";
import "./App.css";

export default function DPLWebsite() {
  const [showPortal, setShowPortal] = useState(false);

  const fleet = [
    {
      title: "Professional Fleet",
      text: "A modern black and orange fleet identity built around clean presentation, realistic specification, and a strong company image.",
    },
    {
      title: "Dual-Port Operations",
      text: "Our company identity is shaped around the movement of freight between the UK and Mainland Europe through realistic port-based logistics.",
    },
    {
      title: "Structured Standards",
      text: "We focus on professionalism, convoy discipline, route planning, and a level of realism that reflects real haulage culture.",
    },
    {
      title: "Community with Purpose",
      text: "DPL is built for drivers who want more than casual trucking and prefer a team with a serious, long-term vision.",
    },
  ];

  const contracts = [
    { route: "Dover → Calais", cargo: "Container Logistics", value: "£12,450" },
    { route: "Hull → Rotterdam", cargo: "General Freight", value: "£13,800" },
    { route: "Felixstowe → Birmingham", cargo: "Retail Distribution", value: "£9,200" },
  ];

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (showPortal) {
    return (
      <div className="portal-page">
        <div className="portal-card">
          <button className="back-link" onClick={() => setShowPortal(false)}>
            ← Back to Website
          </button>
          <h2>DPL Driver Portal</h2>
          <p>
            This section is still under development. For now, use the main website for recruitment,
            fleet information, and company updates.
          </p>
          <div className="portal-actions">
            <a href="https://discord.gg/G9ahKJ5x4w" target="_blank" rel="noreferrer" className="primary-btn">
              Join Discord
            </a>
            <button className="secondary-btn" onClick={() => setShowPortal(false)}>
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="website-shell">
      <header className="site-header">
        <div className="brand-block">
          <div className="brand-mark">DPL</div>
          <div>
            <h1>Dual Port Logistics</h1>
            <p>Professional UK-based virtual haulage</p>
          </div>
        </div>

        <nav className="site-nav">
          <button onClick={() => scrollToSection("about")}>About</button>
          <button onClick={() => scrollToSection("fleet")}>Fleet</button>
          <button onClick={() => scrollToSection("operations")}>Operations</button>
          <button onClick={() => scrollToSection("join")}>Join</button>
          <button className="portal-btn" onClick={() => setShowPortal(true)}>Driver Hub</button>
        </nav>
      </header>

      <section className="hero-section">
        <div className="hero-copy">
          <span className="eyebrow">Logistics • Warehousing • Distribution</span>
          <h2>Built by Drivers, for Drivers.</h2>
          <p>
            Dual Port Logistics is a realistic UK-based virtual trucking company built around professionalism,
            realism, and a fleet image inspired by real-world haulage. From Dover to Hull, DPL brings a strong
            black-and-orange identity to Euro Truck Simulator 2.
          </p>
          <div className="hero-actions">
            <button className="primary-btn" onClick={() => scrollToSection("join")}>Join DPL</button>
            <button className="secondary-btn" onClick={() => scrollToSection("about")}>Learn More</button>
          </div>
        </div>

        <div className="hero-panel">
          <div className="hero-stat-card">
            <strong>20+</strong>
            <span>Years of haulage experience behind the brand</span>
          </div>
          <div className="hero-stat-card">
            <strong>UK Focus</strong>
            <span>Dover and Hull inspired logistics identity</span>
          </div>
          <div className="hero-stat-card">
            <strong>ETS2 Ready</strong>
            <span>Professional fleet presentation and realistic operations</span>
          </div>
        </div>
      </section>

      <section id="about" className="section-block alt-section">
        <div className="section-heading">
          <span className="eyebrow">About Dual Port Logistics</span>
          <h3>20 years in the making. Now loading for the future.</h3>
        </div>
        <div className="about-grid">
          <div className="about-main-card">
            <p>
              Dual Port Logistics isn’t just another virtual trucking company. It is a digital extension of real-world haulage knowledge,
              shaped by years of experience on UK roads, port operations, and general freight movement.
            </p>
            <p>
              We created DPL for drivers who want structure, presentation, and a company identity that feels like a real transport business,
              not just a casual group with trucks.
            </p>
          </div>
          <div className="about-side-card">
            <h4>Our Mission</h4>
            <p>
              To create a realistic VTC environment where professionalism, clean branding, and disciplined driving standards come first.
            </p>
          </div>
          <div className="about-side-card">
            <h4>The Dual-Port Advantage</h4>
            <p>
              DPL reflects the role of a modern UK operator linking British ports to European freight routes with realism and consistency.
            </p>
          </div>
        </div>
      </section>

      <section id="fleet" className="section-block">
        <div className="section-heading">
          <span className="eyebrow">Fleet & Identity</span>
          <h3>A company image built to stand out</h3>
        </div>
        <div className="card-grid">
          {fleet.map((item) => (
            <div className="info-card" key={item.title}>
              <h4>{item.title}</h4>
              <p>{item.text}</p>
            </div>
          ))}
        </div>
      </section>
      
      <section className="section-block fleet-gallery">
        <div className="section-heading">
          <span className="eyebrow">Our Fleet</span>
          <h3>Real trucks. Real identity.</h3>
        </div>

        <div className="fleet-images">
          <img src="/truck1.jpg" alt="DPL Truck 1" />
          <img src="/truck2.jpg" alt="DPL Truck 2" />
          <img src="/truck3.jpg" alt="DPL Truck 3" />
        </div>
      </section>

      <section id="operations" className="section-block operations">
        <div className="section-heading">
          <span className="eyebrow">Operations</span>
          <h3>Representative contract work</h3>
        </div>

        <div className="operations-grid">
          {contracts.map((contract) => (
            <div className="operation-card" key={contract.route}>
              <h4>{contract.route}</h4>
              <p>{contract.cargo}</p>
              <span>{contract.value}</span>
            </div>
          ))}
        </div>
      </section>

      <section id="join" className="section-block join-section">
        <div className="join-card">
          <span className="eyebrow">Join Dual Port Logistics</span>
          <h3>Drive with professionalism. Represent the brand properly.</h3>
          <p>
            Whether you are an experienced ETS2 driver or looking for a serious company to grow with,
            DPL offers a clean, realistic, and long-term environment built around strong standards.
          </p>
          <div className="hero-actions">
            <a href="https://discord.gg/G9ahKJ5x4w" target="_blank" rel="noreferrer" className="primary-btn">
              Join Discord
            </a>
            <a href="https://store.steampowered.com/app/227300/Euro_Truck_Simulator_2/" target="_blank" rel="noreferrer" className="secondary-btn link-btn">
              Get ETS2
            </a>
          </div>
        </div>
      </section>

      <footer className="site-footer">
        <div className="footer-brand">
          <strong>Dual Port Logistics</strong>
          <p>Professional UK-based virtual haulage</p>
        </div>

        <div className="footer-links">
          <a href="#about">About</a>
          <a href="#fleet">Fleet</a>
          <a href="#operations">Operations</a>
          <a href="#join">Join</a>
        </div>

        <p className="footer-copy">© 2026 Dual Port Logistics. All rights reserved.</p>
      </footer>
    </div>
  );
}